import { useCallback, useEffect, useState } from 'react';
import { apiGet, getDernierAbonnementExpire, EVENEMENT_ABONNEMENT_EXPIRE } from '../utils/api';

const MS_PAR_JOUR = 24 * 60 * 60 * 1000;
const DUREE_CACHE_MS = 5 * 60 * 1000;

/** Jours entiers restants avant l'échéance ; négatif si elle est passée. */
function joursAvant(echeance) {
  if (!echeance) return null;
  const date = new Date(echeance);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / MS_PAR_JOUR);
}

function construire({ statut, dateExpiration, type }) {
  const jours = joursAvant(dateExpiration);
  const estExpire = statut === 'expire' || (jours !== null && jours < 0);
  return {
    statut: estExpire ? 'expire' : statut || null,
    type: type || null,
    dateExpiration: dateExpiration || null,
    joursRestants: jours,
    estExpire,
    // Un essai qui se termine dans la semaine : on prévient avant la coupure.
    bientotExpire: !estExpire && statut === 'essai' && jours !== null && jours <= 7,
  };
}

const ETAT_VIDE = {
  statut: null, type: null, dateExpiration: null,
  joursRestants: null, estExpire: false, bientotExpire: false,
};

// ============================================================
// ÉTAT PARTAGÉ
// Le bandeau et l'écran de blocage lisent tous deux cet état, et les composants
// se remontent à chaque navigation : sans mise en commun, chaque page déclencherait
// plusieurs appels identiques. Ici une seule requête sert tout le monde.
// ============================================================
let etatPartage = ETAT_VIDE;
let expirationCache = 0;
let requeteEnVol = null;
let lectureInterdite = false; // 403 : un employé n'a pas accès à cette route, inutile d'insister
const abonnes = new Set();

function publier(etat) {
  etatPartage = etat;
  abonnes.forEach((notifier) => notifier(etat));
}

/** Applique un refus 402 : sa charge utile est la seule information dont dispose un employé. */
function appliquerRefus(donnees) {
  const etat = construire({
    statut: donnees?.statut || 'expire',
    dateExpiration: donnees?.date_expiration,
    type: donnees?.type_abonnement,
  });
  expirationCache = Date.now() + DUREE_CACHE_MS;
  publier(etat);
}

async function charger(forcer = false) {
  if (lectureInterdite && !forcer) return etatPartage;
  if (!forcer && expirationCache > Date.now()) return etatPartage;
  if (requeteEnVol) return requeteEnVol;

  requeteEnVol = (async () => {
    try {
      const reponse = await apiGet('/api/entreprise/subscription-status', { silent: true });
      if (reponse?.success && reponse.subscription) {
        const s = reponse.subscription;
        lectureInterdite = false;
        expirationCache = Date.now() + DUREE_CACHE_MS;
        publier(construire({ statut: s.statut, dateExpiration: s.date_expiration, type: s.type }));
      }
    } catch (erreur) {
      const code = erreur?.status || erreur?.response?.status;
      // 403 : route réservée à l'AdminRH. L'état restera celui du dernier 402.
      if (code === 403) lectureInterdite = true;
      // 402 : api.js a déjà émis l'évènement, l'état est donc déjà à jour.
    } finally {
      requeteEnVol = null;
    }
    return etatPartage;
  })();

  return requeteEnVol;
}

// Un refus 402 survenu n'importe où dans l'application met l'état à jour, une seule fois.
if (typeof window !== 'undefined') {
  window.addEventListener(EVENEMENT_ABONNEMENT_EXPIRE, (evenement) => appliquerRefus(evenement.detail));
  const dejaRefuse = getDernierAbonnementExpire();
  if (dejaRefuse) appliquerRefus(dejaRefuse);
}

/**
 * État de l'abonnement de l'entreprise connectée.
 *
 * Deux sources, parce qu'elles ne sont pas disponibles pour tout le monde :
 *  - `GET /api/entreprise/subscription-status`, réservé à l'AdminRH ;
 *  - le corps du 402 renvoyé par le serveur, seule source dont dispose un employé.
 */
export function useAbonnement() {
  const [etat, setEtat] = useState(etatPartage);
  const [chargement, setChargement] = useState(() => expirationCache <= Date.now() && !lectureInterdite);

  useEffect(() => {
    abonnes.add(setEtat);
    let vivant = true;
    charger().finally(() => {
      if (vivant) setChargement(false);
    });
    return () => {
      vivant = false;
      abonnes.delete(setEtat);
    };
  }, []);

  // Après un paiement transmis : on redemande sans attendre l'expiration du cache.
  const relire = useCallback(async () => {
    setChargement(true);
    lectureInterdite = false;
    try {
      await charger(true);
    } finally {
      setChargement(false);
    }
  }, []);

  return { ...etat, chargement, relire };
}

export default useAbonnement;
